import React, { useState, useEffect, useRef, useContext } from "react";
import "./globalsearch.css";
import { baseRequest } from "../../../axiosconfig";
import { useNavigate, useLocation } from "react-router-dom";
import { UnsavedDataContext } from "../../../context";
import { HELP_CONTENT } from "../../../constants/helpContent";

const TYPE_LABELS = {
  page: "Stranice i uputstva",
  procedure: "Procedure",
  process_map: "Mape procesa",
  business_process: "Poslovni procesi",
  risk_form: "Formulari rizika",
};

function targetUrl(item) {
  switch (item.type) {
    case "page":
      return item.id;
    case "risk_form":
      return `/admin/formulari-rizika/${item.id}`;
    case "process_map":
      return `/admin/mape-procesa/${item.id}`;
    case "procedure":
      return `/admin/mape-procesa/${item.process_map_id}`;
    case "business_process":
      return "/admin/poslovni-procesi";
    default:
      return null;
  }
}

// Pretraga i po uputstvima strana (sadržaj „?“ pomoći). Poređenje je bez
// dijakritike (1:1 po znaku, da indeks pogotka važi i u originalnom tekstu).
const DIACRITICS_MAP = { š: "s", đ: "d", č: "c", ć: "c", ž: "z" };
const normalize = (s) =>
  s.toLowerCase().replace(/[šđčćž]/g, (ch) => DIACRITICS_MAP[ch] || ch);

const HELP_INDEX = Object.entries(HELP_CONTENT).map(([path, content]) => {
  const parts = [content.title];
  for (const section of content.sections || []) {
    if (section.heading) parts.push(section.heading);
    if (section.text) parts.push(section.text);
    if (section.items) parts.push(...section.items);
  }
  return { path, title: content.title, parts, normParts: parts.map(normalize) };
});

function searchHelpPages(query) {
  const q = normalize(query);
  const out = [];
  for (const page of HELP_INDEX) {
    for (let i = 0; i < page.normParts.length; i++) {
      const idx = page.normParts[i].indexOf(q);
      if (idx !== -1) {
        const src = page.parts[i];
        const from = Math.max(0, idx - 25);
        const to = Math.min(src.length, idx + q.length + 45);
        const snippet =
          (from > 0 ? "…" : "") + src.slice(from, to) + (to < src.length ? "…" : "");
        out.push({ type: "page", id: page.path, title: page.title, subtitle: snippet });
        break;
      }
    }
  }
  return out.slice(0, 6);
}

function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const containerRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const { unsavedData, setShowNavigatePopup, setNavigateUrl } =
    useContext(UnsavedDataContext);

  const pageMatches =
    query.trim().length >= 2 ? searchHelpPages(query.trim()) : [];

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults(null);
      setOpen(false);
      return;
    }
    // pogoci po stranama/uputstvima su lokalni pa se prikazuju odmah
    if (searchHelpPages(query.trim()).length > 0) {
      setOpen(true);
    }
    // zaštita od trke: odgovor starijeg (sporijeg) zahteva ne sme da
    // pregazi rezultate novijeg upita
    let cancelled = false;
    const timer = setTimeout(() => {
      baseRequest
        .get("/search", { params: { q: query.trim() } })
        .then((res) => {
          if (cancelled) return;
          setResults(res.data.results || []);
          setOpen(true);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setOpen(true);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const handleSelect = (item) => {
    const url = targetUrl(item);
    if (!url) return;
    setOpen(false);
    setQuery("");
    if (!unsavedData) {
      navigate(url);
    } else if (location.pathname !== url) {
      setShowNavigatePopup(true);
      setNavigateUrl(url);
    }
  };

  const grouped = {};
  for (const item of [...pageMatches, ...(results || [])]) {
    if (!grouped[item.type]) grouped[item.type] = [];
    grouped[item.type].push(item);
  }

  return (
    <div className="global-search" ref={containerRef}>
      <input
        type="text"
        className="global-search-input"
        placeholder="PRETRAGA..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results && setOpen(true)}
      />
      {open && (results || pageMatches.length > 0) && (
        <div className="global-search-results">
          {(results || []).length === 0 && pageMatches.length === 0 && (
            <p className="global-search-empty">Nema rezultata.</p>
          )}
          {Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <p className="global-search-group">{TYPE_LABELS[type] || type}</p>
              <ul>
                {items.map((item) => (
                  <li
                    key={`${item.type}-${item.id}`}
                    className="global-search-item"
                    onClick={() => handleSelect(item)}
                  >
                    <span>{item.title}</span>
                    {item.subtitle && (
                      <span className="global-search-subtitle">
                        {item.subtitle}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GlobalSearch;
